/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import { m11ReportContainerFoiChanged, m11ReportContainerResourceChanged, m11ReportSection10FoiChanged, m11ReportSection10ResourceChanged, m11ResearchStudyResourceChanged, selectM11ReportContainerFoi, selectM11ReportContainerResource, selectM11ReportSection10Foi, selectM11ReportSection10Resource, selectM11ResearchStudyResource } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import jsonEquals from "../../functions/jsonEquals";
import StringEntry from "../../datatypeComponents/StringEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import loadResource from "../m11ReportFunctions/loadResource";
import getIdFromReference from "../../functions/getIdFromReference";
import TextDivEntry from "./TextDivEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveFirstObjectiveChanges = async (dispatch, apiToken, m11ResearchStudyFoi, firstObjectiveState,
    section10State, setAlertModalState,
    setStartingFirstObjectiveState, setStartingSection10State) => {

    let changesStates = {
        firstObjectiveState: firstObjectiveState,
        section10State: section10State
    };
    let response = await saveM11Changes(apiToken, 8, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        if (response.m11ReportContainerResource) {
            dispatch(m11ReportContainerResourceChanged(response.m11ReportContainerResource));
        }
        if (response.m11ReportSection10Resource) {
            dispatch(m11ReportSection10ResourceChanged(response.m11ReportSection10Resource));
        }
        setStartingFirstObjectiveState(JSON.parse(JSON.stringify(firstObjectiveState)));
        setStartingSection10State(JSON.parse(JSON.stringify(section10State)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step008FirstPrimaryObjective = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ReportContainerFoi = useSelector(selectM11ReportContainerFoi);
    const m11ReportContainerResource = useSelector(selectM11ReportContainerResource);
    const m11ReportSection10Foi = useSelector(selectM11ReportSection10Foi);
    const m11ReportSection10Resource = useSelector(selectM11ReportSection10Resource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [firstObjectiveState, setFirstObjectiveState] = useState({
        "objectiveName": "", "objectiveDescription": "", "outcomeMeasure": []
    });
    const [startingFirstObjectiveState, setStartingFirstObjectiveState] = useState({
        "objectiveName": "", "objectiveDescription": "", "outcomeMeasure": []
    });
    const [section10State, setSection10State] = useState({
        "section104Heading": "", "section104Text": "",
        "section104Section": [],
        "section10411Status": "empty", "section10411Xhtml": "",
        "section10412Status": "empty", "section10412Xhtml": "",
        "section10413Status": "empty", "section10413Xhtml": "",
        "section10414Status": "empty", "section10414Xhtml": "",
        "section10415Status": "empty", "section10415Xhtml": ""
    });
    const [startingSection10State, setStartingSection10State] = useState({
        "section104Heading": "", "section104Text": "",
        "section104Section": [],
        "section10411Status": "empty", "section10411Xhtml": "",
        "section10412Status": "empty", "section10412Xhtml": "",
        "section10413Status": "empty", "section10413Xhtml": "",
        "section10414Status": "empty", "section10414Xhtml": "",
        "section10415Status": "empty", "section10415Xhtml": ""
    });
    const [initialCalculationNeeded, setInitialCalculationNeeded] = useState(false);
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && (!jsonEquals(firstObjectiveState, startingFirstObjectiveState) ||
            !jsonEquals(section10State, startingSection10State))),
        async saveAllChanges() {
            await saveFirstObjectiveChanges(dispatch, apiToken, m11ResearchStudyFoi, firstObjectiveState,
                section10State, setAlertModalState,
                setStartingFirstObjectiveState, setStartingSection10State);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource) {
            let startingFirstObjective = {
                "objectiveName": "",
                "objectiveDescription": "",
                "outcomeMeasure": []
            };
            if (m11ResearchStudyResource.objective?.length > 0) {
                let currentFirstObjective = m11ResearchStudyResource.objective[0];
                if (currentFirstObjective.name) {
                    startingFirstObjective.objectiveName = currentFirstObjective.name;
                }
                if (currentFirstObjective.description) {
                    startingFirstObjective.objectiveDescription = currentFirstObjective.description;
                }
                if (currentFirstObjective.outcomeMeasure?.length > 0) {
                    startingFirstObjective.outcomeMeasure = currentFirstObjective.outcomeMeasure;
                }
            }
            startingFirstObjective.loaded = true;
            setFirstObjectiveState(startingFirstObjective);
            setStartingFirstObjectiveState(startingFirstObjective);
            if (m11ResearchStudyResource?.id) {
                let m11ReportFoi = "";
                if (m11ResearchStudyResource.relatesTo?.length > 0) {
                    for (let relatesTo of m11ResearchStudyResource.relatesTo) {
                        if (relatesTo.type?.coding?.[0]?.code === "supported-with" && relatesTo.type.text === "M11 Report" &&
                            relatesTo.targetReference) {
                            m11ReportFoi = getIdFromReference(relatesTo.targetReference);
                        }
                    }
                }
                if (m11ReportFoi && !isNaN(m11ReportFoi) && m11ReportFoi !== m11ReportContainerFoi) {
                    dispatch(m11ReportContainerFoiChanged(m11ReportFoi));
                } else {
                    setInitialCalculationNeeded(true);
                }
            }
            if (Array.isArray(m11ResearchStudyResource.contained) && m11ResearchStudyResource.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of m11ResearchStudyResource.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [m11ResearchStudyResource]);

    useEffect(() => {
        if (initialCalculationNeeded === true) {
            let startingData = {
                "section104Heading": "", "section104Text": "",
                "section104Section": [],
                "section10411Status": "empty", "section10411Xhtml": "",
                "section10412Status": "empty", "section10412Xhtml": "",
                "section10413Status": "empty", "section10413Xhtml": "",
                "section10414Status": "empty", "section10414Xhtml": "",
                "section10415Status": "empty", "section10415Xhtml": "",
                "loaded": true
            };
            setSection10State(startingData);
            setStartingSection10State(startingData);
        }
    }, [initialCalculationNeeded]);

    useEffect(() => {
        if (m11ReportContainerFoi && !isNaN(m11ReportContainerFoi)) {
            loadResource(m11ReportContainerFoi, "M11ReportContainer", apiToken, dispatch);
        }
    }, [m11ReportContainerFoi]);

    useEffect(() => {
        if (m11ReportContainerResource && Object.keys(m11ReportContainerResource).length > 0) {
            if (m11ReportContainerResource.section?.[10]?.entry?.length) {
                let m11ReportSection010Foi = getIdFromReference(m11ReportContainerResource.section[10].entry[0]);
                if (m11ReportSection010Foi && !isNaN(m11ReportSection010Foi) && m11ReportSection010Foi !== m11ReportSection10Foi) {
                    dispatch(m11ReportSection10FoiChanged(m11ReportSection010Foi));
                } else {
                    setInitialCalculationNeeded(true);
                }
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportContainerResource]);

    useEffect(() => {
        if (m11ReportSection10Foi && !isNaN(m11ReportSection10Foi)) {
            loadResource(m11ReportSection10Foi, "M11ReportSection10", apiToken, dispatch);
        }
    }, [m11ReportSection10Foi]);

    useEffect(() => {
        if (m11ReportSection10Resource && Object.keys(m11ReportSection10Resource).length > 0) {
            let startingData = {
                "section104Heading": "", "section104Text": "",
                "section104Section": [],
                "section10411Status": "empty", "section10411Xhtml": "",
                "section10412Status": "empty", "section10412Xhtml": "",
                "section10413Status": "empty", "section10413Xhtml": "",
                "section10414Status": "empty", "section10414Xhtml": "",
                "section10415Status": "empty", "section10415Xhtml": "",
                "loaded": true
            };
            if (m11ReportSection10Resource.section?.[0]?.section?.[3]?.title) {
                startingData.section104Heading = m11ReportSection10Resource.section[0].section[3].title;
            }
            if (m11ReportSection10Resource.section?.[0]?.section?.[3]?.text) {
                startingData.section104Text = m11ReportSection10Resource.section[0].section[3].text;
            }
            if (m11ReportSection10Resource.section?.[0]?.section?.[3]?.section?.length > 0) {
                startingData.section104Section = m11ReportSection10Resource.section[0].section[3].section;
            }
            if (m11ReportSection10Resource.section?.[0]?.section?.[3]?.section?.[0]?.section?.length > 0) {
                if (m11ReportSection10Resource.section[0].section[3].section[0].section[0]?.text?.div &&
                    m11ReportSection10Resource.section[0].section[3].section[0].section[0].text.status !== "empty") {
                    startingData.section10411Status = m11ReportSection10Resource.section[0].section[3].section[0].section[0].text.status;
                    startingData.section10411Xhtml = m11ReportSection10Resource.section[0].section[3].section[0].section[0].text.div;
                }
                if (m11ReportSection10Resource.section[0].section[3].section[0].section[1]?.text?.div &&
                    m11ReportSection10Resource.section[0].section[3].section[0].section[1].text.status !== "empty") {
                    startingData.section10412Status = m11ReportSection10Resource.section[0].section[3].section[0].section[1].text.status;
                    startingData.section10412Xhtml = m11ReportSection10Resource.section[0].section[3].section[0].section[1].text.div;
                }
                if (m11ReportSection10Resource.section[0].section[3].section[0].section[2]?.text?.div &&
                    m11ReportSection10Resource.section[0].section[3].section[0].section[2].text.status !== "empty") {
                    startingData.section10413Status = m11ReportSection10Resource.section[0].section[3].section[0].section[2].text.status;
                    startingData.section10413Xhtml = m11ReportSection10Resource.section[0].section[3].section[0].section[2].text.div;
                }
                if (m11ReportSection10Resource.section[0].section[3].section[0].section[3]?.text?.div &&
                    m11ReportSection10Resource.section[0].section[3].section[0].section[3].text.status !== "empty") {
                    startingData.section10414Status = m11ReportSection10Resource.section[0].section[3].section[0].section[3].text.status;
                    startingData.section10414Xhtml = m11ReportSection10Resource.section[0].section[3].section[0].section[3].text.div;
                }
                if (m11ReportSection10Resource.section[0].section[3].section[0].section[4]?.text?.div &&
                    m11ReportSection10Resource.section[0].section[3].section[0].section[4].text.status !== "empty") {
                    startingData.section10415Status = m11ReportSection10Resource.section[0].section[3].section[0].section[4].text.status;
                    startingData.section10415Xhtml = m11ReportSection10Resource.section[0].section[3].section[0].section[4].text.div;
                }
                setSection10State(startingData);
                setStartingSection10State(startingData);
            } else {
                setInitialCalculationNeeded(true);
            }
        }
    }, [m11ReportSection10Resource]);

    return <div>
        <div className="h3Style h3EntryLabel" style={{ marginBottom: "0px" }}>
            Enter the First Primary Objective.<span> This will be used in Sections 1 (Protocol Summary), 3 (Objectives and Estimands) and 10 (Statistical Considerations).</span>
        </div>
        {!(m11ReportSection10Resource?.id || m11ReportContainerResource?.id) &&
            <h1 style={{ color: "red" }}>Please complete Step 5 (Complete Title Page) before filling in M11 Report Section details.</h1>
        }
        {firstObjectiveState.loaded && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter a name or short label for Primary Objective 1.</span>
            </div>
            <StringEntry elementName='objectiveName'
                required={true}
                fieldLabel='Objective Name'
                startingValue={firstObjectiveState.objectiveName}
                setResourceState={setFirstObjectiveState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter a nontechnical (layperson) description for Primary Objective 1.</span>
            </div>
            <StringEntry elementName='objectiveDescription'
                fieldLabel='Objective Description'
                startingValue={firstObjectiveState.objectiveDescription}
                setResourceState={setFirstObjectiveState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Complete the Estimand Characteristics detail for each outcome and estimand for this objective. Click the </span>+Add Estimand<span> button to enter data for additional estimands.</span>
            </div>
            {!jsonEquals(firstObjectiveState, {
                "objectiveName": "",
                "objectiveDescription": "",
                "outcomeMeasure": []
            }) &&
                <ArrayEntry datatype="M11Estimand" elementName='outcomeMeasure'
                containedResourceDictionary={containedResourceDictionary}
                    doNotShowFieldLabel={true}
                    startCollapsed={true} startEmptyArrayClosed={false}
                    deletable={true} fieldLabel='Estimand'
                    startingValue={firstObjectiveState.outcomeMeasure || []}
                    setResourceState={setFirstObjectiveState} />
            }
        </>}
         <div className="h3Style h3EntryLabel" style={{ marginBottom: "0px" }}>
            Complete Section 10.4.1 Primary Objective 1 (Statistical Considerations).
        </div>
        {section10State.loaded && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Revise the </span>Section 10.4.1.1 Statistical Analysis Method<span> as needed.</span>
            </div>
            <TextDivEntry elementName="section10411Xhtml"
                startingValue={section10State.section10411Xhtml}
                setResourceState={setSection10State} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Revise the </span>Section 10.4.1.2 Handling of Data in Relation to Primary Estimand(s)<span> as needed.</span>
            </div>
            <TextDivEntry elementName="section10412Xhtml"
                startingValue={section10State.section10412Xhtml}
                setResourceState={setSection10State} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Revise the </span>Section 10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s)<span> as needed.</span>
            </div>
            <TextDivEntry elementName="section10413Xhtml"
                startingValue={section10State.section10413Xhtml}
                setResourceState={setSection10State} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Revise the </span>Section 10.4.1.4 Sensitvity Analysis<span> as needed.</span>
            </div>
            <TextDivEntry elementName="section10414Xhtml"
                startingValue={section10State.section10414Xhtml}
                setResourceState={setSection10State} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Revise the </span>Section 10.4.1.5 Supplementary Analysis<span> as needed.</span>
            </div>
            <TextDivEntry elementName="section10415Xhtml"
                startingValue={section10State.section10415Xhtml}
                setResourceState={setSection10State} />
        </>}
    </div>
});

const M11Step008FirstPrimaryObjectiveHelperText = <div>
    <h3>Enter the First Primary Objective</h3>
    <div style={{ textAlign: "left" }}>
        <p>Enter a name or short label for Primary Objective 1. This will be used in the M11 Report Section 1 Protocol Summary.</p>
        <p>Enter a nontechnical (layperson) description for Primary Objective 1. If provided, this will be used in the M11 Report Section 1 Protocol Summary.</p>
        <p>Complete the Estimand Characteristics detail for each outcome and estimand for this objective. Click the +Add Estimand button to enter data for additional estimands.</p>
        <h3>Complete Section 10.4.1 Primary Objective 1 (Statistical Considerations)</h3>
        <div style={{ textAlign: "left" }}>
            <p>Enter data for Section 10.4.1.1 Statistical Analysis Method.</p>
            <div style={{ marginLeft: "24px" }} >
                <p>Describe the statistical analysis methods that will be used to evaluate the first primary objective and associated estimand(s) in Section 3.1.1 Primary Objective 1 and Associated Estimand(s). Ensure that the statistical hypothesis/model/analysis (and corresponding assumptions) is aligned with the primary estimand(s).</p>
                <p>When applicable, state the null and alternative hypotheses, including the pre-planned type 1 error rate, or alternative criteria for evaluating whether the objective has been met, and relevant operating characteristics if appropriate. Describe the statistical model used and the factors that will be included (e.g., covariates and interactions) and any rules for handling these factors (e.g., pooling of centres).</p>
                <p>If modelling and simulation methods are to be used, describe the model (inputs and outputs), the underlying assumptions, and the method of model fitting.</p>
            </div>
            <p>Enter data for Section 10.4.1.2 Handling of Data in Relation to Primary Estimand(s).</p>
            <div style={{ marginLeft: "24px" }} >
                <p>For each intercurrent event of the primary estimand(s) defined in Section 3.1.1 Primary Objective(s) and Associated Estimand(s), explain how data will be handled for the statistical analysis in line with the primary estimand. The handling of intercurrent events in the statistical analysis should be aligned with the specific estimand strategies being used.</p>
                <p>This section should describe in more detail the rationale and handling of the data rather than repeating information from the preceding sections.</p>
            </div>
            <p>Enter data for Section 10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s).</p>
            <div style={{ marginLeft: "24px" }} >
                <p>Describe how missing data will be addressed (e.g., imputation method and model), state the underlying assumptions, and provide a rationale for the approach.</p>
            </div>
            <p>If applicable, enter data for Section 10.4.1.4 Sensitivity Analysis.</p>
            <div style={{ marginLeft: "24px" }} >
                <p>Describe any sensitivity analyses and how their assumptions changed from the assumptions of the main statistical analysis. Sensitivity analyses are a series of analyses conducted with the intent to explore the robustness of inferences from the main estimator to deviations from its underlying modelling assumptions and limitations in the data.</p>
            </div>
            <p>If applicable, enter data for Section 10.4.1.5 Supplementary Analysis.</p>
            <div style={{ marginLeft: "24px" }} >
                <p>Describe any supplementary analysis, if applicable. Supplementary analyses are conducted in addition to the main and sensitivity analyses with the intent to provide additional insights into the understanding of the treatment effect.</p>
            </div>
        </div>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>

// eslint-disable-next-line no-empty-pattern
const M11Step008FirstObjectiveValidationCheck = ({ }) => {

    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [validationCheckState, setValidationCheckState] = useState({});

    useEffect(() => {
        if (m11ResearchStudyFoi) {
            validateStep(apiToken, 8, m11ResearchStudyFoi,
                setValidationCheckState)
        }
    }, [m11ResearchStudyFoi]);

    return <div>{validationCheckState?.success && <div>
        <h4>Step 8 (Enter the First Primary Objective) {validationCheckState.step08numberOfChecks} issues checked. {validationCheckState.step08numberPassed} passed. {validationCheckState.step08numberFailed} failed.</h4>
        <p>Description for first primary objective has data stored in M11ResearchStudy Resource: {validationCheckState.resultCheckObjectiveDescription}</p>
        <p>First primary objective has at least one associated estimand: {validationCheckState.resultCheckObjectiveHasEstimand}</p>
        <p>Each estimand has an Endpoint description: {validationCheckState.resultCheckEstimandHasEndpoint}</p>
        <p>Section 10.4 heading is 'Analyses Associated with the Primary Objective(s)': {validationCheckState.resultCheckSection104Heading}</p>
        <p>Section 10.4 Analyses Associated with the Primary Objective(s) does not have text: {validationCheckState.resultCheckSection104Text}</p>
        <p>Section 10.4.1 heading is 'Primary Objective 1' or 'Primary Objective' if only one primary objective: {validationCheckState.resultCheckSection1041Heading}</p>
        <p>Section 10.4.1 Primary Objective 1 does not have text: {validationCheckState.resultCheckSection1041Content}</p>
        <p>Section 10.4.1.1 heading is 'Statistical Analysis Method': {validationCheckState.resultCheckSection10411Heading}</p>
        <p>Section 10.4.1.1 Statistical Analysis Method has content: {validationCheckState.resultCheckSection10411Content}</p>
        <p>Section 10.4.1.2 heading is 'Handling of Data in Relation to Primary Estimand(s)': {validationCheckState.resultCheckSection10412Heading}</p>
        <p>Section 10.4.1.2 Handling of Data in Relation to Primary Estimand(s) has content: {validationCheckState.resultCheckSection10412Content}</p>
        <p>Section 10.4.1.3 heading is 'Handling of Missing Data in Relation to Primary Estimand(s)': {validationCheckState.resultCheckSection10413Heading}</p>
        <p>Section 10.4.1.3 Handling of Missing Data in Relation to Primary Estimand(s) has content: {validationCheckState.resultCheckSection10413Content}</p>
        <p>If present, Section 10.4.1.4 heading is 'Sensitivity Analysis': {validationCheckState.resultCheckSection10414Heading}</p>
        <p>If present, Section 10.4.1.4 Sensitivity Analysis has content: {validationCheckState.resultCheckSection10414Content}</p>
        <p>If present, Section 10.4.1.5 heading is 'Supplementary Analysis': {validationCheckState.resultCheckSection10415Heading}</p>
        <p>If present, Section 10.4.1.5 Supplementary Analysis has content: {validationCheckState.resultCheckSection10415Content}</p>
    </div>}  </div>
}

export default M11Step008FirstPrimaryObjective;
export { M11Step008FirstPrimaryObjectiveHelperText, M11Step008FirstObjectiveValidationCheck };